import React, { useMemo, useCallback, useState } from "react";
import moment from "moment";
import { DatePicker } from "@alm/flow-ds/components";

import { OnApplyProps } from "elements/types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { State } from "data";
import {
	createGetElementData,
	ElementComponentData,
} from "data/form/elementSelectors";
import { DateElement } from "records/Element";
import { useStandardElement } from "components/Forms/Element";
import { DisplayOnlyValue } from "elements";
import { useDebounceSetDataOnApply } from "hooks/useDebounceSetData";
import { isRTL } from "utils/getLanguageDir";

const DATE_FORMAT = "YYYY-MM-DD";

const validateDate = (date: string) => {
	const enteredDate = moment(date, DATE_FORMAT, true);
	return enteredDate.isValid();
};

interface UseDateElementInput {
	data: ElementComponentData<string>;
	onChange: (value: string | number) => void;
}

const useDateElementInput = ({ data, onChange }: UseDateElementInput) => {
	const { t } = useTranslation();

	const [error, setError] = useState<{
		status: "error" | "valid" | undefined;
		feedback: string[];
	}>({ status: undefined, feedback: [] });

	const [displayError, setDisplayError] = useState<boolean>(false);

	const [displayValue, setDisplayValue] = useState<string>(data.value || "");

	const handleOnChange = useCallback(
		(inputValue: string, calendarEvent: boolean) => {
			if (calendarEvent) {
				onChange(inputValue);
				setError({ status: "valid", feedback: [] });
			} else {
				const isDateValid = validateDate(inputValue);
				const isInputValid = isDateValid || inputValue === "";
				if (isInputValid) {
					onChange(inputValue);
					setError({ status: "valid", feedback: [] });
				} else {
					setError({
						status: "error",
						feedback: [t("elements.date.invalidDate")],
					});
				}
			}
			setDisplayValue(inputValue);
			setDisplayError(false);
		},
		[onChange, t]
	);

	const handleOnBlur = useCallback(
		(currentValue: string) => setDisplayError(true),
		[]
	);

	return {
		handleOnChange,
		handleOnBlur,
		displayValue,
		error: displayError ? error : { status: "valid", feedback: [] },
	};
};

function DateOnApply({
	fid,
	vid,
	id,
	index,
	indexes,
	rowIndex,
	uuid,
	hint,
	label,
	required,
	disabled,
	displayOnly,
	isApplyClicked,
	fieldsIsEditedStatus,
	setFieldsIsEditedStatus,
}: OnApplyProps<DateElement>) {
	const { t, i18n } = useTranslation();
	const getData = useMemo(createGetElementData, []);

	const selectorProps = useMemo(
		() => ({ fid, vid, id, index, indexes, rowIndex, uuid }),
		[fid, vid, id, index, indexes, rowIndex, uuid]
	);

	const data = useSelector((state: State) =>
		getData(state, selectorProps)
	) as ElementComponentData<string>;

	const { eid, setDataAndQueue } = useStandardElement(selectorProps);

	// Setup callbacks for handling debouncing
	const { onChange } = useDebounceSetDataOnApply(
		setDataAndQueue,
		data,
		isApplyClicked,
		fieldsIsEditedStatus[uuid]
	);

	const { handleOnBlur, handleOnChange, displayValue, error } =
		useDateElementInput({ data, onChange });

	const wrappedHandleOnChange = (
		inputValue: string,
		calendarEvent: boolean
	) => {
		handleOnChange(inputValue, calendarEvent);
		if (!fieldsIsEditedStatus[uuid]) {
			setFieldsIsEditedStatus(prev => ({
				...prev,
				[uuid]: true,
			}));
		}
	};

	// Display only
	if (displayOnly) {
		return <DisplayOnlyValue label={label} value={data.value || ""} />;
	}

	return (
		<>
			<DatePicker
				locale={i18n.language}
				onChange={wrappedHandleOnChange}
				onBlur={handleOnBlur}
				monthLabel={t("elements.date.month")}
				yearLabel={t("elements.date.year")}
				nextMonthLabel={t("elements.date.nextMonth")}
				prevMonthLabel={t("elements.date.prevMonth")}
				todayLabel={t("elements.date.today")}
				closeLabel={t("elements.date.close")}
				disabled={disabled}
				feedback={error.feedback}
				hint={hint}
				id={eid}
				label={label}
				required={required}
				status={error.status}
				direction={isRTL() ? "rtl" : "ltr"}
				value={displayValue.toString()}
			/>
		</>
	);
}

export default React.memo(DateOnApply);
